import React, { useState, useEffect } from 'react';
import { Bell, Send, Users, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

const AdminPushNotify = () => {
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const fetchCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/subscribers/count`, { headers: authHeaders() });
      const data = await res.json();
      setSubscriberCount(data.count || 0);
    } catch {
      setSubscriberCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCount(); }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    if (!window.confirm(`Push-Benachrichtigung an ${subscriberCount} Geraete senden?`)) return;

    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/notifications/send`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ title, body, url }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult({ type: 'success', text: data.message });
        setTitle('');
        setBody('');
        setUrl('/');
      } else {
        setResult({ type: 'error', text: data.detail || 'Fehler beim Senden' });
      }
    } catch {
      setResult({ type: 'error', text: 'Verbindungsfehler' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h2 className="font-bold text-gray-900 flex items-center gap-2" data-testid="push-notify-heading">
          <Bell className="w-5 h-5 text-[#0f2440]" />
          Push-Benachrichtigungen
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 flex items-center gap-1.5">
            <Users className="w-4 h-4" />
            {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : subscriberCount} Abonnenten
          </span>
          <button onClick={fetchCount} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors">
            <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="p-6">
        {subscriberCount === 0 && !loading ? (
          <div className="text-center text-gray-400 py-6">
            <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p>Noch keine Push-Abonnenten. Besucher koennen sich ueber das Glocken-Symbol auf der Webseite anmelden.</p>
          </div>
        ) : (
          <form onSubmit={handleSend} className="space-y-4 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                required
                data-testid="push-notify-title"
                placeholder="z.B. Neuer Gottesdienst-Termin"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nachricht</label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                required
                rows={3}
                data-testid="push-notify-body"
                placeholder="z.B. Diesen Sonntag um 16:00 Uhr - Sondergottesdienst"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link (optional)</label>
              <input
                type="text"
                value={url}
                onChange={e => setUrl(e.target.value)}
                data-testid="push-notify-url"
                placeholder="/"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {result && (
              <div className={`p-3 rounded-lg flex items-center gap-2 ${
                result.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`} data-testid="push-notify-result">
                {result.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                <span className="text-sm font-medium">{result.text}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={sending || subscriberCount === 0}
              data-testid="push-notify-send-btn"
              className="px-6 py-2.5 bg-[#0f2440] text-white rounded-lg font-semibold hover:bg-[#1a3a5c] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Wird gesendet...' : `An ${subscriberCount} Geraete senden`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminPushNotify;
